import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KMark } from "@/components/KMark";

export default function Auth() {
  const { login, register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("member");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ ...form, role });
      toast.success(mode === "login" ? "Welcome back." : "Studio-ready.");
      nav("/discover");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-md mx-auto px-6 py-16" data-testid="auth-page">
      <div className="flex justify-center mb-6"><KMark size={56} /></div>
      <p className="overline text-center mb-2">{mode === "login" ? "Sign in" : "Create My KUTZ"}</p>
      <h1 className="font-display text-4xl tracking-tighter text-center text-cream">
        {mode === "login" ? "Welcome back." : "Good hair starts here."}
      </h1>

      <form onSubmit={submit} className="mt-10 space-y-6">
        {mode === "register" && (
          <>
            <div className="flex gap-2" data-testid="role-toggle">
              {["member", "professional"].map((r) => (
                <button
                  type="button" key={r}
                  onClick={() => setRole(r)}
                  data-testid={`role-${r}`}
                  className={`flex-1 py-3 text-xs uppercase tracking-[0.2em] border transition-colors ${role === r ? "border-gold bg-gold text-ink font-semibold" : "border-cream/20 text-cream/80 hover:border-cream/40"}`}
                >
                  {r === "member" ? "I'm a Member" : "Launch My Studio"}
                </button>
              ))}
            </div>
            <input required placeholder="Your name" className="input-line" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-name" />
          </>
        )}
        <input required type="email" placeholder="Email" className="input-line" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
        <input required type="password" placeholder="Password" className="input-line" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="input-password" />
        {err && <p className="text-sm text-red-400" data-testid="auth-error">{err}</p>}
        <button disabled={busy} type="submit" className="btn-gold w-full px-6 py-4 rounded-full text-xs uppercase tracking-[0.25em] font-semibold disabled:opacity-60" data-testid="auth-submit">
          {busy ? "One moment…" : mode === "login" ? "Sign in" : role === "professional" ? "Create My Studio" : "Create account"}
        </button>
      </form>

      <p className="text-center text-cream/60 text-sm mt-8">
        {mode === "login" ? "New to KUTZ? " : "Already have an account? "}
        <button className="text-gold underline" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-auth-mode">
          {mode === "login" ? "Create an account" : "Sign in"}
        </button>
      </p>

      <div className="mt-10 border-t border-cream/10 pt-6">
        <p className="overline mb-2 text-center">Demo accounts</p>
        <p className="text-[11px] text-cream/50 text-center leading-relaxed">
          Ben (Member) · ben@kutz.demo / BenDemo1!<br />
          Steve (Professional) · steve@kutz.demo / ProDemo1!
        </p>
      </div>
    </section>
  );
}
